import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseZap, X } from "lucide-react";
import { hasUnclaimedLegacyData, claimLegacyData, skipLegacyClaimThisSession } from "@/lib/legacyClaim";

// Once-per-device, explicit opt-in migration prompt. Rendered by RequireAuth
// once the signed-in user's uid is available. Never auto-imports — the user
// must tap "Import" for anything to move into their scoped namespace.
export default function LegacyDataClaimModal() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setOpen(hasUnclaimedLegacyData());
  }, []);

  if (!open) return null;

  const onImport = () => {
    setBusy(true);
    try {
      const { claimed } = claimLegacyData();
      toast.success(claimed > 0 ? "Existing device data imported into your account." : "Nothing to import.");
    } catch {
      toast.error("Could not import existing data.");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const onSkip = () => {
    skipLegacyClaimThisSession();
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[10100] flex items-center justify-center p-4"
      style={{ background: "rgba(6,7,9,0.72)" }}
      data-testid="legacy-claim-modal"
    >
      <div
        className="w-full max-w-sm rounded-lg border p-5"
        style={{ background: "var(--rs-bg-2)", borderColor: "var(--rs-border)" }}
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md flex items-center justify-center shrink-0" style={{ background: "rgba(255,179,0,0.14)" }}>
            <DatabaseZap className="w-4.5 h-4.5" style={{ color: "var(--rs-amber)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold">Existing data found on this device</h2>
            <p className="text-[12px] text-[var(--rs-text-3)] mt-1">
              We found RigSentinel data saved on this device before you signed in. Import it into your account now?
            </p>
          </div>
          <button
            data-testid="legacy-claim-close-x"
            onClick={onSkip}
            className="btn-ghost h-7 w-7 rounded-md inline-flex items-center justify-center shrink-0"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button
            data-testid="legacy-claim-skip-btn"
            onClick={onSkip}
            disabled={busy}
            className="h-9 px-3 rounded-md text-[12px] font-semibold text-[var(--rs-text-2)]"
          >
            Not now
          </button>
          <button
            data-testid="legacy-claim-import-btn"
            onClick={onImport}
            disabled={busy}
            className="h-9 px-4 rounded-md text-[12px] font-semibold"
            style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}
          >
            {busy ? "Importing…" : "Import into my account"}
          </button>
        </div>
      </div>
    </div>
  );
}
